import { format, parseISO, addDays } from 'date-fns'
import useCurrentData from '@/views/composable/useCurrentData'

/**
 * タイムカードの「営業日基準の日付解決」モジュール。
 *
 * 背景:
 * 勤怠の開始/終了は HH:mm だけで入力させたい(タブレット向け)が、保存データは
 * 日付付き(YYYY-MM-DDTHH:mm)のため、入力時刻がどの日付に属するかを決める必要がある。
 *
 * バックエンドの営業日モデル(Club#time_difference_seconds / this_day_range):
 * 営業日D = カレンダーD日の開店時刻(openAt) 〜 翌日開店時刻の1秒前、の24時間ウィンドウ。
 * このウィンドウに収まらない日時で保存すると別営業日に帰属してしまうため、
 * フロントの日付解決は必ずこのウィンドウ内の日付を選ぶ必要がある。
 *
 * 本モジュールのルール:
 * - 開始: 深夜跨ぎ店舗では「開店時刻(openAt)より前の時刻=営業日+1(カレンダー翌日)」、
 *   開店時刻以降は営業日当日。バックエンドの営業日ウィンドウと一致する。
 *   ※開店前の早出(例: 開店20時の店の18時出勤)は「カレンダー翌日の18:00」として
 *     保存される(営業日相対エンコード)。実時刻とは異なるが、これが本システムの
 *     営業日ウィンドウに収まる唯一の表現であり、既存データもこの形式。
 * - 終了: 開始と同じ日付で組み立て、開始より前になる場合のみ+1日(深夜0時跨ぎ退勤)。
 *   従来は終了も開始と無関係にopenAt境界で解決されており、開始が営業日+1に
 *   エンコードされた場合(早出)に開始>終了となって保存不能になるバグがあった。
 * - 非跨ぎ店舗ではどちらも補正しない(前後関係の誤りはバリデーションに委ねる)
 *
 * default export はクラブ設定(openAt/closeAt)込みの composable。
 * 純粋関数(resolveStartDateTime / resolveEndDateTime)も named export しており、
 * クラブ情報を自前で持つ画面やテストから直接利用できる。
 */

// canonical な日時文字列(YYYY-MM-DDTHH:mm[:ss])から HH:mm を取り出す。取れなければ null。
export const extractTime = value => {
  if (!value) return null
  const m = String(value).match(/[T ](\d{2}):(\d{2})/)

  return m ? `${m[1]}:${m[2]}` : null
}

const buildDateTime = (dateStr, time) => `${dateStr}T${time}`

// クラブのopenAt/closeAt(ISO日時文字列)から HH:mm を取り出す。無効なら null。
const clubTimeOf = (club, key) => {
  const value = club?.[key]
  if (!value) return null
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) return null

  return format(parsed, 'HH:mm')
}

/**
 * クラブが深夜跨ぎ営業(開店時刻 > 閉店時刻)かを判定する。
 * useBusinessDateAdjustment もこの判定を共用している(判定ロジックの重複防止)。
 * @param {object} club currentClub 相当のオブジェクト(openAt / closeAt を持つ)
 */
export const isClubCrossingMidnight = club => {
  const openTime = clubTimeOf(club, 'openAt')
  const closeTime = clubTimeOf(club, 'closeAt')
  if (!openTime || !closeTime) return false

  return openTime > closeTime
}

/**
 * 開始日時の日付部分を営業日ウィンドウ(開店時刻境界)に収まるよう解決する。
 * @param {string} businessDate 営業日(YYYY-MM-DD)
 * @param {string} dateTime 入力由来の日時(YYYY-MM-DDTHH:mm。日付部分は仮でよい)
 * @param {boolean} isCrossingMidnight 深夜跨ぎ店舗か
 * @param {string|null} openTime 開店時刻(HH:mm)
 * @returns {string} 日付を確定した日時(解決不能な場合は入力をそのまま返す)
 */
export const resolveStartDateTime = ({
  businessDate, dateTime, isCrossingMidnight, openTime,
}) => {
  const time = extractTime(dateTime)
  if (!time || !businessDate) return dateTime

  if (isCrossingMidnight && openTime && time < openTime) {
    // 開店時刻より前=営業日+1(深夜帯、および開店前早出の営業日相対エンコード)。
    // 例: 閉店05:00の店の01:30出勤 → 営業日+1の01:30 / 開店20:00の店の18:00早出 → 営業日+1の18:00
    return format(addDays(parseISO(businessDate), 1), `yyyy-MM-dd'T'${time}`)
  }

  // 開店時刻以降・非跨ぎ店舗は営業日当日
  return buildDateTime(businessDate, time)
}

/**
 * 終了日時の日付部分を開始日時基準で解決する。
 * @param {string} startAt 確定済みの開始日時(YYYY-MM-DDTHH:mm)
 * @param {string} dateTime 入力由来の終了日時(YYYY-MM-DDTHH:mm。日付部分は仮でよい)
 * @param {boolean} allowNextDay 深夜0時跨ぎの+1日補正を許可するか(深夜跨ぎ店舗のみtrue)
 * @returns {string} 日付を確定した日時(解決不能な場合は入力をそのまま返す)
 */
export const resolveEndDateTime = ({ startAt, dateTime, allowNextDay }) => {
  const time = extractTime(dateTime)
  if (!time || !startAt) return dateTime

  const startDate = parseISO(startAt)
  if (Number.isNaN(startDate.getTime())) return dateTime

  const baseDateStr = format(startDate, 'yyyy-MM-dd')
  const candidate = buildDateTime(baseDateStr, time)

  // 比較は分精度で行う。startAt に秒が含まれる場合(例: 18:00:30)に、同時刻分の
  // 終了(18:00)が「開始より前」と誤判定されて翌日に飛ぶのを防ぐ
  const startMinutePrecision = format(startDate, "yyyy-MM-dd'T'HH:mm")
  if (allowNextDay && candidate < startMinutePrecision) {
    // 開始より前になる場合は深夜0時跨ぎとみなして翌日にする。例: 18:00開始の00:30退勤
    return format(addDays(parseISO(baseDateStr), 1), `yyyy-MM-dd'T'${time}`)
  }

  return candidate
}

/**
 * クラブ設定(openAt/closeAt)込みの composable。
 * @param {Ref<string>} businessDateRef 営業日(YYYY-MM-DD)のref
 */
export default businessDateRef => {
  const { currentClub } = useCurrentData()

  const isCrossingMidnight = () => isClubCrossingMidnight(currentClub.value)

  const resolveStartAt = dateTime => resolveStartDateTime({
    businessDate: businessDateRef.value,
    dateTime,
    isCrossingMidnight: isCrossingMidnight(),
    openTime: clubTimeOf(currentClub.value, 'openAt'),
  })

  const resolveLeaveAt = (startAt, dateTime) => resolveEndDateTime({
    startAt,
    dateTime,
    allowNextDay: isCrossingMidnight(),
  })

  return {
    isCrossingMidnight,
    resolveStartAt,
    resolveLeaveAt,
  }
}
